import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  TextField,
  Divider,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  alpha,
  Fade,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  AccountBalanceWallet as WalletIcon,
  History as HistoryIcon,
  TrendingUp as CreditIcon,
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import {
  walletHistory,
  AddwalletMoney,
} from "../../services/api/walletService";
import { loadRazorpayScript } from "../../pages/razorPay/razorpay";
import { useSelector } from "react-redux";

// Constants
const MIN_AMOUNT = 10;
const MAX_AMOUNT = 50000;
const PRESET_AMOUNTS = [100, 500, 1000, 5000];
const ROWS_PER_PAGE = 10;
const SCROLL_THRESHOLD = 100;
const SNACKBAR_DURATION = 3000;

const WalletDrawer = ({ open, onClose, userId }) => {
  // State
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [addMoneyDialogOpen, setAddMoneyDialogOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Refs
  const hasFetchedWallet = useRef(false);
  const tableContainerRef = useRef(null);
  const user = useSelector((state) => state?.auth?.user);

  // Fetch wallet data when drawer opens
  useEffect(() => {
    if (open && userId && !hasFetchedWallet.current) {
      hasFetchedWallet.current = true;
      fetchWalletHistory(true);
    }

    if (!open) {
      hasFetchedWallet.current = false;
      setPage(0);
      setTransactions([]);
      setHasMore(true);
    }
  }, [open, userId]);

  // Set up scroll listener for infinite scroll
  useEffect(() => {
    const container = tableContainerRef.current?.parentElement?.parentElement;
    if (!container || activeTab !== 0) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD &&
        !loadingMore &&
        hasMore &&
        !loading
      ) {
        loadMoreTransactions();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [activeTab, loadingMore, hasMore, loading]);

  // Core functions
  const fetchWalletHistory = useCallback(
    async (reset = false, silent = false) => {
      if (reset) {
        setPage(0);
        setTransactions([]);
        setHasMore(true);
      }

      setLoadingMore(silent || !reset);
      if (reset && !silent) setLoading(true);

      try {
        const currentPage = reset ? 0 : page;
        const payload = {
          userId: user?.userId,
          limit: ROWS_PER_PAGE,
          offset: currentPage * ROWS_PER_PAGE,
        };

        const response = await walletHistory(payload);

        if (response.data?.success && response.data?.data) {
          setWalletData(response.data.data.wallet);
          const newTransactions = response.data.data.transactions || [];
          const totalCount = response.data.data.transactions?.length || 0;

          if (reset) {
            setTransactions(newTransactions);
          } else {
            setTransactions((prev) => [...prev, ...newTransactions]);
          }

          setTotalTransactions(totalCount);
          setHasMore(newTransactions.length === ROWS_PER_PAGE);
        } else {
          showSnackbar(
            response.data?.message || "Failed to load wallet data",
            "error"
          );
        }
      } catch (error) {
        console.error("Error fetching wallet history:", error);
        if (!silent) {
          showSnackbar("Failed to load wallet data", "error");
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [page, user?.userId]
  );

  const loadMoreTransactions = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = page + 1;
    setPage(nextPage);

    try {
      setLoadingMore(true);
      const payload = {
        userId: user?.userId,
        limit: ROWS_PER_PAGE,
        offset: nextPage * ROWS_PER_PAGE,
      };

      const response = await walletHistory(payload);

      if (response.data?.success && response.data?.data) {
        const newTransactions = response.data.data.transactions || [];
        setTransactions((prev) => [...prev, ...newTransactions]);
        setHasMore(newTransactions.length === ROWS_PER_PAGE);
      }
    } catch (error) {
      console.error("Error loading more transactions:", error);
      showSnackbar("Failed to load more transactions", "error");
    } finally {
      setLoadingMore(false);
    }
  }, [page, loadingMore, hasMore, user?.userId]);

  // Razorpay integration
  const openRazorpayDirectly = useCallback(
    async ({ amount: amountToAdd, userDetails }) => {
      const isLoaded = await loadRazorpayScript();

      if (!isLoaded || typeof window.Razorpay !== "function") {
        showSnackbar(
          "Failed to load payment gateway. Please refresh and try again.",
          "error"
        );
        setPaymentProcessing(false);
        setSubmitting(false);
        return;
      }

      const amountInPaise = Math.round(amountToAdd * 100);
      const orderId = `wallet_${userId}_${Date.now()}`;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_rhCfeloZpwX3eY",
        amount: amountInPaise,
        currency: "INR",
        name: "Zippshoppe",
        description: `Add ₹${amountToAdd} to wallet`,
        image: "/logo.png",
        handler: async (response) => {
          console.log("Razorpay payment success:", response);

          try {
            const walletPayload = {
              userId,
              amount: parseInt(amountToAdd),
              paymentMode: "Razorpay",
              transactionId: response.razorpay_payment_id,
              createdBy: userId,
              referenceId: response.razorpay_order_id || orderId,
              referenceType: "topup",
              paymentStatus: "successful",
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            };

            console.log("Calling AddwalletMoney after Razorpay success:", walletPayload);
            const walletResponse = await AddwalletMoney(walletPayload);

            if (walletResponse.data?.success) {
              showSnackbar(`₹${amountToAdd} added successfully!`, "success");
              setAddMoneyDialogOpen(false);
              setAmount("");
              setAmountError("");
              await fetchWalletHistory(true);
            } else {
              showSnackbar(
                walletResponse.data?.message ||
                  "Payment successful but wallet update failed. Please contact support.",
                "error"
              );
            }
          } catch (error) {
            console.error("Error updating wallet after payment:", error);
            showSnackbar(
              "Payment successful but failed to update wallet. Please contact support.",
              "error"
            );
          } finally {
            setPaymentProcessing(false);
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => {
            console.log("Razorpay modal closed - payment cancelled by user");
            setPaymentProcessing(false);
            setSubmitting(false);
            showSnackbar("Payment cancelled", "info");
            setAddMoneyDialogOpen(false);
            setAmount("");
            setAmountError("");
          },
        },
        prefill: {
          name: userDetails?.name || "Customer",
          email: userDetails?.email || "customer@example.com",
          contact: userDetails?.phone || "9999999999",
        },
        theme: { color: "#f6cc4f" },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", async (error) => {
        console.error("Payment failed:", error);
        setPaymentProcessing(false);
        setSubmitting(false);
        showSnackbar("Payment failed. Please try again.", "error");

        try {
          const failedPayload = {
            userId,
            amount: parseInt(amountToAdd),
            paymentMode: "Razorpay",
            createdBy: userId,
            referenceType: "topup",
            paymentStatus: "failed",
            errorDetails: error,
          };
          await AddwalletMoney(failedPayload);
        } catch (recordError) {
          console.error("Error recording failed payment:", recordError);
        }
      });

      rzp.open();
    },
    [userId, fetchWalletHistory]
  );

  const initializeWalletPayment = useCallback(async () => {
    setPaymentProcessing(true);

    try {
      const amountNum = parseFloat(amount);
      await openRazorpayDirectly({
        amount: amountNum,
        userDetails: {
          name: user?.name || "",
          email: user?.email || "",
          phone: user?.phoneNumber || user?.phone || "",
        },
      });
    } catch (error) {
      console.error("Error initializing wallet payment:", error);
      showSnackbar("Failed to initialize payment", "error");
      setPaymentProcessing(false);
      setSubmitting(false);
    }
  }, [amount, user, openRazorpayDirectly]);

  const handleAddMoney = useCallback(async () => {
    const amountNum = parseFloat(amount);

    if (isNaN(amountNum) || amountNum <= 0) {
      setAmountError("Please enter a valid amount");
      return;
    }
    if (amountNum < MIN_AMOUNT) {
      setAmountError(`Minimum amount is ₹${MIN_AMOUNT}`);
      return;
    }
    if (amountNum > MAX_AMOUNT) {
      setAmountError(`Maximum amount is ₹${MAX_AMOUNT}`);
      return;
    }

    setAmountError("");
    setSubmitting(true);
    await initializeWalletPayment();
  }, [amount, initializeWalletPayment]);

  // UI helper functions
  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar((prev) => ({ ...prev, open: false })), SNACKBAR_DURATION);
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const formatAmount = useCallback((amountValue) => {
    const num = parseFloat(amountValue);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }, []);

  const getTransactionTypeIcon = useCallback((type) => {
    switch (type?.toLowerCase()) {
      case "credit":
        return <CreditIcon sx={{ fontSize: 20, color: "#4caf50" }} />;
      case "debit":
        return <PaymentIcon sx={{ fontSize: 20, color: "#f44336" }} />;
      default:
        return <ReceiptIcon sx={{ fontSize: 20, color: "#999" }} />;
    }
  }, []);

  const getTransactionTypeColor = useCallback((type) => {
    return type?.toLowerCase() === "credit" ? "#4caf50" : "#f44336";
  }, []);

  const getPaymentStatusColor = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case "successful":
        return "#4caf50";
      case "pending":
        return "#ff9800";
      case "failed":
        return "#f44336";
      default:
        return "#999";
    }
  }, []);

  // Transaction item component
  const TransactionItem = useCallback(
    ({ transaction }) => (
      <TableRow hover>
        <TableCell sx={{ fontSize: 12 }}>{formatDate(transaction.createdOn)}</TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {getTransactionTypeIcon(transaction.transactionType)}
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: getTransactionTypeColor(transaction.transactionType),
                textTransform: "capitalize",
              }}
            >
              {transaction.transactionType}
            </Typography>
          </Box>
        </TableCell>
        <TableCell align="right">
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: getTransactionTypeColor(transaction.transactionType),
            }}
          >
            {transaction.transactionType === "credit" ? "+" : "-"}
            {formatAmount(transaction.transactionAmount)}
          </Typography>
          <Typography sx={{ fontSize: 10, color: "#999" }}>
            Balance: {formatAmount(transaction.balanceAfter)}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={transaction.paymentStatus}
            size="small"
            sx={{
              height: 22,
              fontSize: 10,
              fontWeight: 600,
              bgcolor: alpha(getPaymentStatusColor(transaction.paymentStatus), 0.1),
              color: getPaymentStatusColor(transaction.paymentStatus),
            }}
          />
        </TableCell>
      </TableRow>
    ),
    [formatDate, getTransactionTypeIcon, getTransactionTypeColor, formatAmount, getPaymentStatusColor]
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500, md: 550 },
            maxWidth: "100%",
            borderRadius: { xs: 0, sm: "24px 0 0 24px" },
            overflow: "hidden",
            bgcolor: "#fafafa",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "linear-gradient(135deg, #f6cc4f 0%, #ffe8a2 50%, #f6cc4f 100%)",
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <WalletIcon sx={{ fontSize: 28, color: "#3a2e00" }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, fontSize: 20, color: "#3a2e00" }}
            >
              My Wallet
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: "rgba(0,0,0,0.08)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.15)" },
            }}
          >
            <CloseIcon sx={{ color: "#3a2e00" }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Wallet Balance Card */}
              {walletData && (
                <Fade in>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 4,
                      mb: 3,
                      background: "linear-gradient(135deg, #1a237e 0%, #283593 50%, #1a237e 100%)",
                      color: "#fff",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: -50,
                        right: -50,
                        width: 150,
                        height: 150,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.1)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: -30,
                        left: -30,
                        width: 100,
                        height: 100,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.08)",
                      }}
                    />

                    <Typography sx={{ fontSize: 14, opacity: 0.8, mb: 1 }}>
                      Total Balance
                    </Typography>
                    <Typography sx={{ fontSize: 36, fontWeight: 800, mb: 2 }}>
                      {formatAmount(walletData.walletBalance)}
                    </Typography>

                    <Stack direction="row" spacing={3}>
                      <Box>
                        <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
                          Available
                        </Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                          {formatAmount(walletData.availableBalance)}
                        </Typography>
                      </Box>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
                      />
                      <Box>
                        <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
                          Reserved
                        </Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                          {formatAmount(walletData.reservedBalance)}
                        </Typography>
                      </Box>
                    </Stack>

                    {walletData.lastTransactionDate && (
                      <Typography sx={{ fontSize: 11, opacity: 0.6, mt: 2 }}>
                        Last updated: {formatDate(walletData.lastTransactionDate)}
                      </Typography>
                    )}
                  </Paper>
                </Fade>
              )}

              {/* Add Money Button */}
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddMoneyDialogOpen(true)}
                sx={{
                  borderRadius: 3,
                  bgcolor: "#f6cc4f",
                  color: "#3a2e00",
                  py: 1.5,
                  fontWeight: 700,
                  "&:hover": { bgcolor: "#c7a749" },
                }}
              >
                Add Money
              </Button>

              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{
                  // mt: 3,
                  mb: 2,
                  borderBottom: "1px solid #e0e0e0",
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 14,
                  },
                  "& .Mui-selected": { color: "#1a237e" },
                  "& .MuiTabs-indicator": { bgcolor: "#1a237e" },
                }}
              >
                <Tab label="Transaction History" icon={<HistoryIcon />} iconPosition="start" />
              </Tabs>

              {/* Transaction History */}
              {activeTab === 0 && (
                <Box
                  ref={tableContainerRef}
                  sx={{ maxHeight: "calc(100vh - 400px)", overflowY: "auto" }}
                >
                  {transactions.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 5 }}>
                      <HistoryIcon sx={{ fontSize: 60, color: "#ccc", mb: 2 }} />
                      <Typography sx={{ color: "#666" }}>No transactions yet</Typography>
                      <Typography sx={{ color: "#999", fontSize: 13 }}>
                        Add money to see your transaction history
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3 }}>
                        <Table size="small">
                          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">
                                Amount
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {transactions.map((transaction, index) => (
                              <TransactionItem
                                key={transaction.id || index}
                                transaction={transaction}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {loadingMore && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      )}

                      {!hasMore && transactions.length > 0 && (
                        <Typography
                          sx={{ textAlign: "center", py: 2, color: "#999", fontSize: 12 }}
                        >
                          No more transactions to load
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Drawer>

      {/* Add Money Dialog */}
      <Dialog
        open={addMoneyDialogOpen}
        onClose={() => {
          if (!submitting && !paymentProcessing) {
            setAddMoneyDialogOpen(false);
            setAmount("");
            setAmountError("");
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WalletIcon sx={{ color: "#f6cc4f" }} />
            Add Money to Wallet
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#666", mb: 2 }}>
            Enter the amount you want to add. The amount will be credited to
            your wallet instantly.
          </Typography>

          <TextField
            label="Amount (₹)"
            fullWidth
            type="number"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setAmountError("");
            }}
            error={!!amountError}
            helperText={amountError}
            disabled={submitting || paymentProcessing}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontWeight: 700 }}>₹</Typography>
                </InputAdornment>
              ),
            }}
            autoFocus
            sx={{ mb: 3 }}
          />

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {PRESET_AMOUNTS.map((presetAmount) => (
              <Button
                key={presetAmount}
                variant="outlined"
                size="small"
                onClick={() => {
                  setAmount(presetAmount.toString());
                  setAmountError("");
                }}
                disabled={submitting || paymentProcessing}
                sx={{
                  borderRadius: 2,
                  borderColor: "#f6cc4f",
                  color: "#3a2e00",
                  "&:hover": {
                    borderColor: "#f6cc4f",
                    bgcolor: alpha("#f6cc4f", 0.05),
                  },
                }}
              >
                ₹{presetAmount}
              </Button>
            ))}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ bgcolor: alpha("#f6cc4f", 0.1), p: 2, borderRadius: 2 }}>
            <Typography
              sx={{
                fontSize: 12,
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 16, color: "#4caf50" }} />
              Secure payment via Razorpay
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#666", mt: 1 }}>
              You will be redirected to Razorpay payment gateway to complete the
              transaction.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => {
              setAddMoneyDialogOpen(false);
              setAmount("");
              setAmountError("");
            }}
            disabled={submitting || paymentProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddMoney}
            disabled={
              submitting || paymentProcessing || !amount || parseFloat(amount) <= 0
            }
            variant="contained"
            sx={{
              bgcolor: "#f6cc4f",
              color: "#3a2e00",
              "&:hover": { bgcolor: "#ffe8a2" },
            }}
          >
            {submitting || paymentProcessing ? (
              <CircularProgress size={24} />
            ) : (
              "Proceed to Pay"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={SNACKBAR_DURATION}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WalletDrawer;